import { useCallback, useState } from "react";

import type {
  CreateQueueRequest,
  NatsTransportSpec,
  ProvisionModeUI,
  UpdateQueueRequest,
} from "../../../types";

type QueueType = "rabbitmq" | "nats" | "kafka" | "pulsar";

/** Per-stream form state — mirrors the fields previously on QueueFormState. */
export interface StreamFormState {
  streamName: string;
  subjectPrefix: string;
  commands: string;
  lifecycleEvents: string;
  deadLetter: string;
  apiConsumer: string;
  workerConsumer: string;
}

export interface QueueFormState {
  name: string;
  queueType: QueueType;
  mode: ProvisionModeUI;
  // RabbitMQ
  replicas: 1 | 3;
  persistence: boolean;
  managementUi: boolean;
  // NATS — single-stream legacy fields (kept for backward compat tests)
  jetStream: boolean;
  natsSubjectPrefix: string;
  natsStreamName: string;
  natsCommands: string;
  natsLifecycleEvents: string;
  natsDeadLetter: string;
  natsApiConsumer: string;
  natsWorkerConsumer: string;
  // NATS — multi-stream entries
  streams: StreamFormState[];
  // Kafka
  partitions: number;
  replicationFactor: 1 | 3;
  // Advanced
  tls: boolean;
  auth: "none" | "token" | "mtls";
}

function createEmptyStream(): StreamFormState {
  return {
    streamName: "",
    subjectPrefix: "",
    commands: "commands",
    lifecycleEvents: "lifecycle",
    deadLetter: "dead_letter",
    apiConsumer: "api_consumer",
    workerConsumer: "worker_consumer",
  };
}

/** Convert a NatsTransportSpec (from API) to StreamFormState (form). */
function transportSpecToStreamState(spec: NatsTransportSpec): StreamFormState {
  return {
    streamName: spec.streamName,
    subjectPrefix: spec.subjectPrefix,
    commands: spec.commandSubjects.join(", "),
    lifecycleEvents: spec.lifecycleSubjects.join(", "),
    deadLetter: spec.deadLetterSubject,
    apiConsumer: spec.apiConsumer.durableName,
    workerConsumer: spec.workerConsumer.durableName,
  };
}

export interface UseQueueFormOptions {
  /** Edit mode: existing streams are read-only, only new streams can be added. */
  isEdit?: boolean;
  /** Existing stream specs to display as read-only in edit mode. */
  existingStreams?: NatsTransportSpec[];
  /** Resource name in edit mode (read-only). */
  resourceName?: string;
}

export const INITIAL_QUEUE_STATE: QueueFormState = {
  name: "",
  queueType: "rabbitmq",
  mode: "shared",
  replicas: 3,
  persistence: true,
  managementUi: true,
  jetStream: true,
  natsSubjectPrefix: "",
  natsStreamName: "",
  natsCommands: "commands",
  natsLifecycleEvents: "lifecycle",
  natsDeadLetter: "dead_letter",
  natsApiConsumer: "api_consumer",
  natsWorkerConsumer: "worker_consumer",
  streams: [createEmptyStream()],
  partitions: 3,
  replicationFactor: 3,
  tls: false,
  auth: "token",
};

export interface QueueFormErrors {
  name?: string;
  natsTransport?: string;
  streamErrors?: (string | undefined)[];
}

const NAME_RE = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

const parseSubjects = (value: string): string[] =>
  value
    .split(/[,\n]/)
    .map((subject) => subject.trim())
    .filter(Boolean);

/** Check whether a stream entry has any user-entered content. */
function isStreamStarted(s: StreamFormState): boolean {
  return Boolean(
    s.streamName.trim() ||
    s.subjectPrefix.trim() ||
    s.commands.trim() !== "commands" ||
    s.lifecycleEvents.trim() !== "lifecycle" ||
    s.deadLetter.trim() !== "dead_letter" ||
    s.apiConsumer.trim() !== "api_consumer" ||
    s.workerConsumer.trim() !== "worker_consumer",
  );
}

/** Validate a single stream entry; returns an error message or undefined. */
function validateStream(s: StreamFormState): string | undefined {
  if (!s.streamName.trim()) return "Stream name is required";
  if (!s.subjectPrefix.trim()) return "Subject prefix is required";
  if (parseSubjects(s.commands).length === 0)
    return "At least one command subject is required";
  if (parseSubjects(s.lifecycleEvents).length === 0)
    return "At least one lifecycle subject is required";
  if (!s.deadLetter.trim()) return "Dead-letter subject is required";
  if (!s.apiConsumer.trim()) return "API consumer name is required";
  if (!s.workerConsumer.trim()) return "Worker consumer name is required";
  return undefined;
}

export function useQueueForm(options: UseQueueFormOptions = {}) {
  const { isEdit = false, existingStreams = [], resourceName = "" } = options;

  // In edit mode, pre-load existing streams as read-only entries plus one
  // empty editable entry for the first new stream.
  const editInitialState: QueueFormState = {
    ...INITIAL_QUEUE_STATE,
    name: resourceName,
    queueType: "nats",
    streams: isEdit
      ? [
          ...existingStreams.map(transportSpecToStreamState),
          createEmptyStream(),
        ]
      : [createEmptyStream()],
  };

  const [state, setState] = useState<QueueFormState>(
    isEdit ? editInitialState : INITIAL_QUEUE_STATE,
  );
  // Number of leading streams that are read-only (existing in edit mode).
  const readOnlyCount = isEdit ? existingStreams.length : 0;
  const [errors, setErrors] = useState<QueueFormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const validateField = useCallback((field: string, value: string) => {
    if (field === "name") {
      if (!value.trim()) return "Queue name is required";
      if (!NAME_RE.test(value))
        return "Start with a letter or underscore; use only letters, numbers, and underscores. Hyphens are not allowed. Example: test_pg_db (not test-pg-db)";
      if (value.length > 50) return "Name must be 50 characters or less";
    }
    return undefined;
  }, []);

  const setField = useCallback(
    <K extends keyof QueueFormState>(field: K, value: QueueFormState[K]) => {
      setState((s) => ({ ...s, [field]: value }));
      if (touched[field as string]) {
        const err = validateField(field as string, String(value));
        setErrors((e) => ({ ...e, [field as string]: err }));
      }
    },
    [touched, validateField],
  );

  const onBlur = useCallback(
    (field: string) => {
      setTouched((t) => ({ ...t, [field]: true }));
      const err = validateField(
        field,
        String(state[field as keyof QueueFormState] ?? ""),
      );
      setErrors((e) => ({ ...e, [field as string]: err }));
    },
    [state, validateField],
  );

  // ── Multi-stream helpers ──────────────────────────────────────────

  const addStream = useCallback(() => {
    setState((s) => ({ ...s, streams: [...s.streams, createEmptyStream()] }));
  }, []);

  const removeStream = useCallback(
    (index: number) => {
      setState((s) => {
        // Cannot remove read-only (existing) streams or the last editable stream.
        if (index < readOnlyCount) return s;
        const editableCount = s.streams.length - readOnlyCount;
        if (editableCount <= 1) return s;
        return { ...s, streams: s.streams.filter((_, i) => i !== index) };
      });
    },
    [readOnlyCount],
  );

  const updateStream = useCallback(
    <K extends keyof StreamFormState>(
      index: number,
      field: K,
      value: StreamFormState[K],
    ) => {
      setState((s) => ({
        ...s,
        streams: s.streams.map((stream, i) =>
          i === index ? { ...stream, [field]: value } : stream,
        ),
      }));
    },
    [],
  );

  // ── Validation ────────────────────────────────────────────────────

  const validate = useCallback((): boolean => {
    const newErrors: QueueFormErrors = {};
    const nameErr = validateField("name", state.name);
    if (nameErr) newErrors.name = nameErr;

    if (state.queueType === "nats") {
      // In edit mode, only validate new (editable) streams.
      const startIdx = isEdit ? readOnlyCount : 0;
      const streamErrs: (string | undefined)[] = isEdit
        ? new Array(readOnlyCount).fill(undefined)
        : [];
      const editableStreams = state.streams.slice(startIdx);
      const editableErrs = editableStreams.map(validateStream);
      streamErrs.push(...editableErrs);

      // Check for duplicate stream names among started new entries
      // (also check against existing read-only names).
      const seenNames = new Set<string>(
        isEdit
          ? state.streams
              .slice(0, readOnlyCount)
              .map((s) => s.streamName.trim())
          : [],
      );
      for (let i = startIdx; i < state.streams.length; i++) {
        const name = state.streams[i].streamName.trim();
        if (name && seenNames.has(name)) {
          streamErrs[i] = "Duplicate stream name";
        }
        if (name) seenNames.add(name);
      }

      const hasErrors = streamErrs.some(Boolean);

      if (hasErrors) {
        newErrors.streamErrors = streamErrs;
        newErrors.natsTransport =
          "Complete all NATS transport fields before creating the resource";
      }
    }

    setErrors(newErrors);
    setTouched({ name: true });
    return Object.keys(newErrors).length === 0;
  }, [state, validateField]);

  // ── Build request ─────────────────────────────────────────────────

  const buildRequest = useCallback((): CreateQueueRequest => {
    const req: CreateQueueRequest = {
      name: state.name,
      queueType: state.queueType,
      mode: state.mode,
    };

    if (state.queueType === "nats") {
      const buildTransport = (s: StreamFormState) => {
        const commands = parseSubjects(s.commands);
        const lifecycleSubjects = parseSubjects(s.lifecycleEvents);
        return {
          subjectPrefix: s.subjectPrefix.trim(),
          streamName: s.streamName.trim(),
          commandSubjects: commands,
          lifecycleSubjects,
          deadLetterSubject: s.deadLetter.trim(),
          apiConsumer: {
            durableName: s.apiConsumer.trim(),
            filterSubjects: lifecycleSubjects,
          },
          workerConsumer: {
            durableName: s.workerConsumer.trim(),
            filterSubjects: commands,
          },
        };
      };

      // Single stream → natsTransport (backward compat)
      // Multiple streams → natsStreams array
      const startedStreams = state.streams.filter(isStreamStarted);
      if (startedStreams.length <= 1) {
        req.natsTransport = buildTransport(state.streams[0]);
      } else {
        req.natsStreams = state.streams.map(buildTransport);
      }
    }

    if (state.mode === "dedicated") {
      if (state.queueType === "rabbitmq") {
        req.replicas = state.replicas;
        req.persistence = state.persistence;
        req.managementUi = state.managementUi;
      }
      if (state.queueType === "nats") {
        req.jetStream = state.jetStream;
      }
      if (state.queueType === "kafka") {
        req.partitions = state.partitions;
        req.replicationFactor = state.replicationFactor;
      }
    }
    if (state.tls) req.tls = state.tls;
    if (state.auth !== "token") req.auth = state.auth;
    return req;
  }, [state]);

  // ── Build update request (edit mode) ──────────────────────────────

  const buildUpdateRequest = useCallback((): UpdateQueueRequest => {
    const buildTransport = (s: StreamFormState) => {
      const commands = parseSubjects(s.commands);
      const lifecycleSubjects = parseSubjects(s.lifecycleEvents);
      return {
        subjectPrefix: s.subjectPrefix.trim(),
        streamName: s.streamName.trim(),
        commandSubjects: commands,
        lifecycleSubjects,
        deadLetterSubject: s.deadLetter.trim(),
        apiConsumer: {
          durableName: s.apiConsumer.trim(),
          filterSubjects: lifecycleSubjects,
        },
        workerConsumer: {
          durableName: s.workerConsumer.trim(),
          filterSubjects: commands,
        },
      };
    };

    // Only include new (editable) streams that have content.
    const newStreams = state.streams
      .slice(readOnlyCount)
      .filter(isStreamStarted)
      .map(buildTransport);

    return { natsStreams: newStreams };
  }, [state, readOnlyCount]);

  return {
    state,
    errors,
    isEdit,
    readOnlyCount,
    setField,
    onBlur,
    validate,
    buildRequest,
    buildUpdateRequest,
    addStream,
    removeStream,
    updateStream,
  };
}
